// Funzioni di utilità per formattazione date e valute

/**
 * Formatta una data in formato italiano
 * @param {string|Date} data - La data da formattare
 * @returns {string} - Data formattata in formato DD/MM/YYYY
 */
export const formatDate = (data) => {
  if (!data) return 'N/A';
  
  try {
    const date = new Date(data);
    if (isNaN(date.getTime())) return 'Data non valida';
    
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Errore formattazione data:', error);
    return 'Errore data';
  }
};

/**
 * Formatta una data con ora in formato italiano
 * @param {string|Date} data - La data da formattare
 * @returns {string} - Data formattata in formato DD/MM/YYYY HH:MM
 */
export const formatDateTime = (data) => {
  if (!data) return 'N/A';
  
  try {
    const date = new Date(data);
    if (isNaN(date.getTime())) return 'Data non valida';
    
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.error('Errore formattazione data/ora:', error);
    return 'Errore data';
  }
};

/**
 * Formatta un importo in valuta italiana
 * @param {number|string} importo - L'importo da formattare
 * @param {object} options - Opzioni di formattazione
 * @returns {string} - Importo formattato in euro
 */
export const formatCurrency = (importo, options = {}) => {
  if (importo === null || importo === undefined || importo === '') return '€0,00';
  
  const {
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    showSymbol = true
  } = options;
  
  try {
    const numero = parseFloat(importo);
    if (isNaN(numero)) return '€0,00';
    
    const formatted = numero.toLocaleString('it-IT', {
      minimumFractionDigits,
      maximumFractionDigits
    });
    
    return showSymbol ? `€${formatted}` : formatted;
  } catch (error) {
    console.error('Errore formattazione valuta:', error);
    return '€0,00';
  }
};

/**
 * Formatta un numero con separatori delle migliaia
 * @param {number|string} numero - Il numero da formattare
 * @returns {string} - Numero formattato
 */
export const formatNumber = (numero) => {
  if (numero === null || numero === undefined || numero === '') return '0';
  
  try {
    const num = parseFloat(numero);
    if (isNaN(num)) return '0';
    
    return num.toLocaleString('it-IT');
  } catch (error) {
    console.error('Errore formattazione numero:', error);
    return '0';
  }
};

/**
 * Calcola la differenza in giorni tra due date
 * @param {string|Date} dataInizio - Data di inizio
 * @param {string|Date} dataFine - Data di fine
 * @returns {number} - Differenza in giorni
 */
export const calcolaGiorniDifferenza = (dataInizio, dataFine) => {
  try {
    const inizio = new Date(dataInizio);
    const fine = new Date(dataFine);
    
    if (isNaN(inizio.getTime()) || isNaN(fine.getTime())) return 0;
    
    const differenza = fine.getTime() - inizio.getTime();
    return Math.ceil(differenza / (1000 * 60 * 60 * 24));
  } catch (error) {
    console.error('Errore calcolo differenza giorni:', error);
    return 0;
  }
};

/**
 * Verifica se una data è nel passato
 * @param {string|Date} data - La data da verificare
 * @returns {boolean} - True se la data è nel passato
 */
export const isDataPassata = (data) => {
  try {
    const dataVerifica = new Date(data);
    const oggi = new Date();
    oggi.setHours(0, 0, 0, 0);
    
    return dataVerifica < oggi;
  } catch (error) {
    console.error('Errore verifica data passata:', error);
    return false;
  }
};

/**
 * Formatta una data per input HTML
 * @param {string|Date} data - La data da formattare
 * @returns {string} - Data in formato YYYY-MM-DD
 */
export const formatDateForInput = (data) => {
  if (!data) return '';
  
  try {
    const date = new Date(data);
    if (isNaN(date.getTime())) return '';
    
    return date.toISOString().split('T')[0];
  } catch (error) {
    console.error('Errore formattazione data per input:', error);
    return '';
  }
};

/**
 * Ottiene il nome del mese in italiano
 * @param {number} mese - Numero del mese (0-11)
 * @returns {string} - Nome del mese
 */
export const getNomeMese = (mese) => {
  const mesi = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];
  
  return mesi[mese] || 'Mese non valido';
};

/**
 * Ottiene il nome del giorno della settimana in italiano
 * @param {number} giorno - Numero del giorno (0-6, dove 0 = domenica)
 * @returns {string} - Nome del giorno
 */
export const getNomeGiorno = (giorno) => {
  const giorni = [
    'Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 
    'Giovedì', 'Venerdì', 'Sabato'
  ];
  
  return giorni[giorno] || 'Giorno non valido';
};

/**
 * Calcola il tempo trascorso dalla data fornita in formato leggibile italiano
 * @param {Date|string} data - La data da cui calcolare il tempo trascorso
 * @returns {string} - Tempo trascorso in formato "X minuti fa", "X ore fa", etc.
 */
export const tempoTrascorso = (data) => {
  if (!data) return '';
  
  const ora = new Date();
  const dataNotifica = new Date(data);
  const differenzaMs = ora - dataNotifica;
  
  // Se la differenza è negativa (data futura), restituisci stringa vuota
  if (differenzaMs < 0) return '';
  
  const minuti = Math.floor(differenzaMs / (1000 * 60));
  const ore = Math.floor(differenzaMs / (1000 * 60 * 60));
  const giorni = Math.floor(differenzaMs / (1000 * 60 * 60 * 24));
  const settimane = Math.floor(giorni / 7);
  const mesi = Math.floor(giorni / 30);
  
  if (minuti < 1) {
    return 'Ora';
  } else if (minuti < 60) {
    return `${minuti} min fa`;
  } else if (ore < 24) {
    return ore === 1 ? '1 ora fa' : `${ore} ore fa`;
  } else if (giorni < 7) {
    return giorni === 1 ? '1 giorno fa' : `${giorni} giorni fa`;
  } else if (settimane < 4) {
    return settimane === 1 ? '1 settimana fa' : `${settimane} settimane fa`;
  } else if (mesi < 12) {
    return mesi === 1 ? '1 mese fa' : `${mesi} mesi fa`;
  } else {
    const anni = Math.floor(mesi / 12);
    return anni === 1 ? '1 anno fa' : `${anni} anni fa`;
  }
}; 